import type { TextFieldProps } from '@mui/material';
import { Box, TextField } from '@mui/material';
import { IconButton, colors } from '@sweep-io/sweep-design';
import { Search as MagnifyingGlassIcon } from '@sweep-io/sweep-design/dist/icons/Search';
import { Close as CloseIcon } from '@sweep-io/sweep-design/dist/icons/Close';
import React, { useEffect, useRef } from 'react';

export interface SearchInputProps extends Pick<TextFieldProps, 'onChange' | 'value'> {
  withFixedMagnifyingGlassIcon?: boolean;
  TextFieldProps?: TextFieldProps;
  dataTestId?: string;
  placeholder?: string;
  onClearButtonClick?: () => void;
  endAdornment?: React.ReactNode;
  forceCloseButton?: boolean;
  autoFocus?: boolean;
  variant?: 'default' | 'dark';
  size?: 'default' | 'small';
}

export const SearchInput = ({
  onClearButtonClick,
  withFixedMagnifyingGlassIcon,
  TextFieldProps = {},
  dataTestId,
  placeholder,
  endAdornment,
  forceCloseButton,
  autoFocus,
  onChange,
  value,
  variant = 'default',
  size = 'default',
}: SearchInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isSmall = size === 'small';
  const isDark = variant === 'dark';

  // Somehow the MUI autoFocus is not working, so we need to use a ref to focus the input
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Small delay to ensure the component is fully mounted and rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [autoFocus]);
  const inputSx = {
    backgroundColor: isDark ? 'rgba(222, 223, 224, 0.24)' : undefined,
    minHeight: isSmall ? '32px' : '40px',
    input: {
      paddingBlock: isSmall ? '5px' : undefined,
    },
    '.MuiOutlinedInput-notchedOutline': {
      border: isDark ? 'none' : undefined,
    },
  };

  const { InputProps: TextFieldInputProps, ..._TextFieldProps } = TextFieldProps;

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        data-testid={dataTestId}
        size="small"
        fullWidth
        inputRef={inputRef}
        className="search-input"
        placeholder={placeholder || 'Search'}
        InputProps={{
          sx: {
            paddingLeft: withFixedMagnifyingGlassIcon ? '25px' : 0,
            '&::placeholder': {
              color: colors.grey[700],
            },
            ...inputSx,
          },
          startAdornment: withFixedMagnifyingGlassIcon ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <MagnifyingGlassIcon
                size="medium"
                color={isDark ? colors.grey[700] : colors.grey[800]}
              />
            </Box>
          ) : null,
          endAdornment: (
            <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 1 }}>
              {endAdornment && endAdornment}
              {(forceCloseButton || (!!TextFieldProps.value && TextFieldProps.value !== '')) &&
                onClearButtonClick && (
                  <IconButton variant="flat" size="tiny" onClick={onClearButtonClick}>
                    <CloseIcon />
                  </IconButton>
                )}
            </Box>
          ),
          ...TextFieldInputProps,
        }}
        {...{ onChange, value, ..._TextFieldProps }}
      />
    </Box>
  );
};
